import { Button, Drawer, Empty, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../../hooks/use-date-formatter.tsx"
import { getEnvelopeEvents } from "../../api/tracking.services.ts"
import { parseApiTime } from "../../helpers/api-time.ts"
import { isCountersigner } from "../../helpers/countersigner.ts"
import type {
  EnvelopeEventRow,
  TrackedEnvelope
} from "../../types/tracking.types.ts"

const { Text } = Typography

interface Props {
  envelope: TrackedEnvelope
  onClose: () => void
}

/**
 * The envelope's signing activity, in the order it happened. Anyone who can read
 * the envelope sees what happened, to whom and when; the address and browser
 * come only when the API sends them (the reader holds `document_view_activity`).
 */
export function EnvelopeActivityDrawer({ envelope, onClose }: Props) {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()
  const [events, setEvents] = useState<EnvelopeEventRow[] | null>(null)
  const [next, setNext] = useState<number | null>(null)
  const [failed, setFailed] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [reloads, setReloads] = useState(0)
  // Bumped whenever the envelope changes, on retry and on unmount, so a page requested
  // before that never lands; the ref also stops a second click while one is in flight.
  const generation = useRef(0)
  const inFlight = useRef(false)

  const load = (page: number) => {
    const mine = generation.current
    return getEnvelopeEvents(envelope.id, page).then((result) => {
      if (mine !== generation.current) return
      inFlight.current = false
      setLoadingMore(false)
      if (!result.ok || !result.data) {
        setFailed(true)
        setEvents((current) => current ?? [])
        return
      }
      const incoming = result.data.results
      setFailed(false)
      setEvents((current) => {
        const kept = page === 1 ? [] : (current ?? [])
        const seen = new Set(kept.map((event) => event.id))
        return [...kept, ...incoming.filter((event) => !seen.has(event.id))]
      })
      setNext(result.data.next ? page + 1 : null)
    })
  }

  const loadMore = () => {
    if (next === null || inFlight.current) return
    inFlight.current = true
    setLoadingMore(true)
    setFailed(false)
    void load(next)
  }

  useEffect(() => {
    generation.current += 1
    inFlight.current = false
    setEvents(null)
    setNext(null)
    setFailed(false)
    setLoadingMore(false)
    void load(1)
    return () => {
      generation.current += 1
    }
  }, [envelope.id, reloads])

  const internalIds = new Set(
    envelope.recipients.filter(isCountersigner).map((recipient) => recipient.id)
  )
  // The internal recipient is named after the company; what happened on it was done by the team member.
  const who = (event: EnvelopeEventRow) => {
    if (event.recipient && event.actor && internalIds.has(event.recipient.id))
      return event.actor.name
    if (event.recipient) return event.recipient.name
    return event.actor
      ? t("esign.tracking.activity.contractor", { name: event.actor.name })
      : ""
  }
  const line = (event: EnvelopeEventRow) => {
    if (event.event_type === "sent" && event.recipient) {
      return t("esign.tracking.activity.sentTo", { name: event.recipient.name })
    }
    const what = t(`esign.tracking.activity.events.${event.event_type}`, {
      defaultValue: event.event_type_label
    })
    return [who(event), what].filter(Boolean).join(" · ")
  }
  const evidenceLine = (
    evidence: NonNullable<EnvelopeEventRow["evidence"]>
  ) => {
    const parts = [
      evidence.ip_address
        ? t("esign.tracking.activity.fromAddress", { ip: evidence.ip_address })
        : "",
      evidence.user_agent
    ].filter(Boolean)
    return parts.length
      ? parts.join(" · ")
      : t("esign.tracking.activity.nothingRecorded")
  }

  return (
    <Drawer
      title={t("esign.tracking.activity.title", {
        document: envelope.subject.name
      })}
      open
      onClose={onClose}
      width={"min(480px, 100vw)"}
      maskClosable
    >
      {events === null ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : failed && events.length === 0 ? (
        <Empty description={t("esign.tracking.activity.loadFailed")}>
          <Button
            size={"small"}
            onClick={() => setReloads((count) => count + 1)}
          >
            {t("esign.tracking.activity.retry")}
          </Button>
        </Empty>
      ) : events.length === 0 ? (
        <Empty description={t("esign.tracking.activity.empty")} />
      ) : (
        <Flex vertical gap={10}>
          {events.map((event) => {
            const at = parseApiTime(event.created)
            return (
              <Flex
                key={event.id}
                vertical
                className={"border-b border-gray-100 pb-2"}
              >
                <Flex justify={"space-between"} gap={8}>
                  <Text className={"text-sm"}>{line(event)}</Text>
                  <Text
                    type={"secondary"}
                    className={"whitespace-nowrap text-xs"}
                  >
                    {at ? formatDate(at.toISOString()) : ""}
                  </Text>
                </Flex>
                {event.evidence && (
                  <Text type={"secondary"} className={"text-xs"}>
                    {evidenceLine(event.evidence)}
                  </Text>
                )}
              </Flex>
            )
          })}
          {failed && (
            <Text type={"danger"} className={"text-xs"}>
              {t("esign.tracking.activity.loadFailed")}
            </Text>
          )}
          {next && (
            <Button
              size={"small"}
              loading={loadingMore}
              disabled={loadingMore}
              onClick={loadMore}
            >
              {failed
                ? t("esign.tracking.activity.retry")
                : t("esign.tracking.activity.loadMore")}
            </Button>
          )}
        </Flex>
      )}
    </Drawer>
  )
}
